/** Route registry (spec §10.3): drives the sidebar, the bottom bar, guards and Permissions › Preview.
 * Pure data, so the Preview tab can evaluate it for any role. Elements are attached in router.tsx. */
import { Bot, Boxes, Building2, ClipboardCheck, FileSpreadsheet, FileText, Inbox, House, ListChecks, Package, ReceiptText, Tags, ScrollText, Settings, ShieldCheck, Users, type LucideIcon } from 'lucide-react'
import { matchPath } from 'react-router'
import { type Perms, canAll } from '@/lib/permissions'

export type NavGroup = 'main' | 'work' | 'catalogue' | 'admin'

/** Sidebar order; every group but 'main' gets a heading (D33). */
export const NAV_GROUPS: readonly NavGroup[] = ['main', 'work', 'catalogue', 'admin']

export type RouteDef = {
  path: string
  screenKey: string
  module: string
  /** All of these keys are needed; empty means any logged-in user. */
  permissions: readonly string[]
  nav?: { group: NavGroup; labelKey: string; icon: LucideIcon }
}

export const ROUTES: readonly RouteDef[] = [
  { path: '/', screenKey: 'core.home', module: 'core', permissions: [], nav: { group: 'main', labelKey: 'core.nav.home', icon: House } },
  { path: '/me', screenKey: 'accounts.my_details', module: 'accounts', permissions: [] },
  {
    path: '/organizations',
    screenKey: 'organizations.list',
    module: 'organizations',
    permissions: ['organizations.access', 'organizations.view'],
    nav: { group: 'work', labelKey: 'core.nav.organizations', icon: Building2 },
  },
  {
    path: '/organizations/:id',
    screenKey: 'organizations.detail',
    module: 'organizations',
    permissions: ['organizations.access', 'organizations.view'],
  },
  {
    path: '/enquiries',
    screenKey: 'enquiries.list',
    module: 'enquiries',
    permissions: ['enquiries.access'],
    nav: { group: 'work', labelKey: 'core.nav.enquiries', icon: Inbox },
  },
  { path: '/enquiries/new', screenKey: 'enquiries.new', module: 'enquiries', permissions: ['enquiries.access', 'enquiries.create'] },
  { path: '/enquiries/:id', screenKey: 'enquiries.detail', module: 'enquiries', permissions: ['enquiries.access'] },
  {
    path: '/quotes',
    screenKey: 'quotes.list',
    module: 'quotes',
    permissions: ['quotes.access'],
    nav: { group: 'work', labelKey: 'core.nav.quotes', icon: FileText },
  },
  { path: '/quotes/new', screenKey: 'quotes.new', module: 'quotes', permissions: ['quotes.access', 'quotes.create'] },
  { path: '/quotes/:id', screenKey: 'quotes.detail', module: 'quotes', permissions: ['quotes.access'] },
  {
    path: '/tasks',
    screenKey: 'tasks.list',
    module: 'tasks',
    permissions: ['tasks.access'],
    nav: { group: 'work', labelKey: 'core.nav.tasks', icon: ListChecks },
  },
  { path: '/tasks/new', screenKey: 'tasks.new', module: 'tasks', permissions: ['tasks.access', 'tasks.create'] },
  { path: '/tasks/:id', screenKey: 'tasks.detail', module: 'tasks', permissions: ['tasks.access'] },
  {
    path: '/invoices',
    screenKey: 'sales.invoices',
    module: 'sales',
    permissions: ['invoices.access', 'invoices.view_all'],
    nav: { group: 'work', labelKey: 'core.nav.invoices', icon: ReceiptText },
  },
  { path: '/invoices/:id', screenKey: 'sales.invoice', module: 'sales', permissions: ['invoices.access', 'invoices.view_all'] },
  {
    path: '/products',
    screenKey: 'products.list',
    module: 'products',
    permissions: ['products.access', 'products.view'],
    nav: { group: 'catalogue', labelKey: 'core.nav.products', icon: Package },
  },
  {
    path: '/products/:id',
    screenKey: 'products.detail',
    module: 'products',
    permissions: ['products.access', 'products.view'],
  },
  {
    path: '/stock',
    screenKey: 'stock.list',
    module: 'stock',
    permissions: ['stock.access', 'stock.view'],
    nav: { group: 'catalogue', labelKey: 'core.nav.stock', icon: Boxes },
  },
  {
    path: '/catalogue/lists',
    screenKey: 'products.lists',
    module: 'products',
    permissions: ['products.access', 'products.view'],
    nav: { group: 'catalogue', labelKey: 'core.nav.lists', icon: Tags },
  },
  {
    path: '/admin/users',
    screenKey: 'accounts.users',
    module: 'accounts',
    permissions: ['users.manage'],
    nav: { group: 'admin', labelKey: 'core.nav.users', icon: Users },
  },
  { path: '/admin/users/:id', screenKey: 'accounts.user_detail', module: 'accounts', permissions: ['users.manage'] },
  {
    path: '/admin/permissions',
    screenKey: 'accounts.permissions',
    module: 'accounts',
    permissions: ['roles.manage'],
    nav: { group: 'admin', labelKey: 'core.nav.permissions', icon: ShieldCheck },
  },
  {
    path: '/admin/audit',
    screenKey: 'audit.list',
    module: 'audit',
    permissions: ['audit.access', 'audit.view'],
    nav: { group: 'admin', labelKey: 'core.nav.audit', icon: ScrollText },
  },
  {
    path: '/imports',
    screenKey: 'imports.list',
    module: 'imports',
    permissions: ['imports.access'],
    nav: { group: 'admin', labelKey: 'core.nav.imports', icon: FileSpreadsheet },
  },
  { path: '/imports/new', screenKey: 'imports.new', module: 'imports', permissions: ['imports.access', 'imports.create'] },
  { path: '/imports/:id', screenKey: 'imports.job', module: 'imports', permissions: ['imports.access'] },
  {
    path: '/import-review',
    screenKey: 'import_review.reviews',
    module: 'import_review',
    permissions: ['import_review.access'],
    nav: { group: 'admin', labelKey: 'core.nav.import_review', icon: ClipboardCheck },
  },
  { path: '/import-review/:id', screenKey: 'import_review.review', module: 'import_review', permissions: ['import_review.access'] },
  {
    path: '/ace',
    screenKey: 'ace.reports',
    module: 'ace',
    permissions: ['ace.access', 'ace.view'],
    nav: { group: 'admin', labelKey: 'core.nav.ace', icon: Bot },
  },
  {
    path: '/admin/settings',
    screenKey: 'core.settings',
    module: 'core',
    permissions: ['settings.access'],
    nav: { group: 'admin', labelKey: 'core.nav.settings', icon: Settings },
  },
  { path: '/help/:module', screenKey: 'core.help', module: 'core', permissions: [] },
]

/** Navigation entries the permissions allow, in registry order. */
export function visibleRoutes(perms: Perms): RouteDef[] {
  return ROUTES.filter((route) => route.nav && canAll(perms, route.permissions))
}

export function routeForPath(pathname: string): RouteDef | undefined {
  return ROUTES.find((route) => matchPath({ path: route.path, end: true }, pathname))
}
